import * as sharedTypes from 'src/actions/shared/sharedTypes';
import ssoConfigResource, {
  AttrToInboxMapping,
  AttrToRoleMapping,
  MetadataChoice,
  SSOConfig,
} from './ssoConfigResource';
import { matchesError } from 'src/models/DomainError';

const validateRoleMapping = (mapping: AttrToRoleMapping, prefix: string) => {
  const errors: any[] = [];

  if (!mapping.attributeKey || mapping.attributeKey.trim() === '') {
    errors.push({
      attr: `${prefix}.attributeKey`,
      code: 'blank',
      detail: 'SAML attribute is required',
    });
  }

  if (!mapping.attributeValue || mapping.attributeValue.trim() === '') {
    errors.push({
      attr: `${prefix}.attributeValue`,
      code: 'blank',
      detail: 'SAML attribute value is required',
    });
  }

  if (!mapping.roleIds || !Array.isArray(mapping.roleIds) || mapping.roleIds.length === 0) {
    errors.push({
      attr: `${prefix}.roleIds`,
      code: 'blank',
      detail: 'At least one role must be selected',
    });
  }

  return errors;
};

const validateInboxMapping = (mapping: AttrToInboxMapping, prefix: string) => {
  const errors: any[] = [];

  if (!mapping.attributeKey || mapping.attributeKey.trim() === '') {
    errors.push({
      attr: `${prefix}.attributeKey`,
      code: 'blank',
      detail: 'SAML attribute is required',
    });
  }

  if (!mapping.attributeValue || mapping.attributeValue.trim() === '') {
    errors.push({
      attr: `${prefix}.attributeValue`,
      code: 'blank',
      detail: 'SAML attribute value is required',
    });
  }

  if (!mapping.routingGroupIds || !Array.isArray(mapping.routingGroupIds) || mapping.routingGroupIds.length === 0) {
    errors.push({
      attr: `${prefix}.routingGroupIds`,
      code: 'blank',
      detail: 'At least one inbox must be selected',
    });
  }

  return errors;
};

const validateMappingDuplicates = (mappings: any[], baseAttrPath: string) => {
  const errors: any[] = [];
  const seenMappings = new Set();

  mappings.forEach((mapping, index) => {
    if (mapping.attributeKey && mapping.attributeValue) {
      const key = `${mapping.attributeKey}:${mapping.attributeValue}`;
      if (seenMappings.has(key)) {
        errors.push({
          attr: `${baseAttrPath}[${index}]`,
          code: 'takes',
          detail: 'This attribute key and value combination already exists',
        });
      } else {
        seenMappings.add(key);
      }
    }
  });

  return errors;
};

const validateIdpAttributeMappingsConfig = (config: SSOConfig) => {
  let errors: any[] = [];

  const idpConfig = config.idpAttributeMappingsConfig;

  if (config.jitUserCreation) {
    if (!idpConfig?.defaultInboxes || idpConfig?.defaultInboxes.length === 0) {
      errors = errors.concat({
        attr: 'idpAttributeMappingsConfig.defaultInboxes',
        code: 'blank',
        detail: 'At least one inbox should be selected',
      });
    }

    if (!idpConfig?.defaultRoles || idpConfig?.defaultRoles.length === 0) {
      errors = errors.concat({
        attr: 'idpAttributeMappingsConfig.defaultRoles',
        code: 'blank',
        detail: 'At least one role should be selected',
      });
    }
  }

  if (idpConfig?.roleMappings && Array.isArray(idpConfig.roleMappings)) {
    // Validate individual role mappings
    idpConfig.roleMappings.forEach((mapping, index) => {
      errors = errors.concat(validateRoleMapping(mapping, `idpAttributeMappingsConfig.roleMappings[${index}]`));
    });

    // Check for duplicate role mappings
    errors = errors.concat(
      validateMappingDuplicates(idpConfig.roleMappings, 'idpAttributeMappingsConfig.roleMappings')
    );
  }

  if (idpConfig?.inboxMappings && Array.isArray(idpConfig.inboxMappings)) {
    // Validate individual inbox mappings
    idpConfig.inboxMappings.forEach((mapping, index) => {
      errors = errors.concat(validateInboxMapping(mapping, `idpAttributeMappingsConfig.inboxMappings[${index}]`));
    });

    // Check for duplicate inbox mappings
    errors = errors.concat(
      validateMappingDuplicates(idpConfig.inboxMappings, 'idpAttributeMappingsConfig.inboxMappings')
    );
  }

  return errors;
};

export const validateSSOConfig: sharedTypes.AppThunk = (config: SSOConfig) => dispatch => {
  if (!config.enabled) {
    return true;
  }

  const validationError = ssoConfigResource.actions.validationError;
  let errors: any[] = [];

  if (config.metadataOption === MetadataChoice.URL && !config.metadataURL) {
    errors = errors.concat({
      attr: 'metadataURL',
      code: 'blank',
      detail: 'Please enter a valid URL for identity provider',
    });
  }

  if (config.metadataOption === MetadataChoice.DATA && !config.metadata) {
    errors = errors.concat({
      attr: 'metadata',
      code: 'blank',
      detail: 'Please enter metadata for identity provider',
    });
  }

  if (config.idpAttributeMappingsConfig || config.jitUserCreation) {
    errors = errors.concat(validateIdpAttributeMappingsConfig(config));
  }

  if (errors.length) {
    dispatch(validationError(errors));
    return false;
  }

  return true;
};

export const getErrorMessage = err => {
  if (
    matchesError(err, { attr: 'identityProviderMetadataURL', code: 'invalid' }) ||
    matchesError(err, { attr: 'identityProviderMetadataURL', code: 'not_exist' })
  ) {
    return 'Check the URL entered for the identity provider';
  } else if (
    matchesError(err, { attr: 'identityProviderMetadataURL', code: 'unexpected_error' }) ||
    matchesError(err, { attr: 'identityProviderMetadataURL', code: 'temporary_failure' })
  ) {
    return 'Could not connect to the identity provider';
  } else if (err.attr && err.attr.includes('idpAttributeMappingsConfig')) {
    return err.detail;
  }
  return err.detail;
};
